import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { KEYWORD_PRESETS } from '../constants/presets'

interface PresetKeywordButtonsProps {
  onSelectPreset: (keywords: string[]) => void
  /**
   * `grid` is the empty-state layout. `compact` is a single row of chips for the
   * sidebar, where presets now live permanently — previously they were reachable
   * only from an empty state that auto-search filled in before anyone saw it.
   */
  variant?: 'grid' | 'compact'
}

export function PresetKeywordButtons({
  onSelectPreset,
  variant = 'grid',
}: PresetKeywordButtonsProps) {
  if (variant === 'compact') {
    return (
      <div>
        <h3 className="mb-2 text-sm font-medium text-muted-foreground">빠른 시작</h3>
        <div className="flex flex-wrap gap-2">
          {KEYWORD_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => onSelectPreset(preset.keywords)}
              title={preset.description}
              className="flex min-h-11 items-center gap-1.5 rounded-full border bg-card px-3 text-sm transition-colors hover:border-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:min-h-0 lg:py-1.5"
            >
              <span aria-hidden="true">{preset.icon}</span>
              {preset.name}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="mt-8 w-full max-w-4xl">
      <h3 className="mb-4 text-center text-lg font-semibold text-muted-foreground">
        또는 인기 프리셋으로 시작하기
      </h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {/*
          A real <button>, not a Card with onClick. shadcn's Card renders a div, so
          the previous version had no role, no tabIndex and no key handler — the
          preset grid was unreachable by keyboard (WCAG 2.1.1).
        */}
        {KEYWORD_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            onClick={() => onSelectPreset(preset.keywords)}
            className="rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Card className="h-full transition-all hover:border-primary hover:shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <span className="text-xl" aria-hidden="true">
                    {preset.icon}
                  </span>
                  {preset.name}
                </CardTitle>
                <CardDescription className="text-xs">{preset.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1">
                  {preset.keywords.slice(0, 4).map((keyword) => (
                    <span
                      key={keyword}
                      className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium"
                    >
                      {keyword}
                    </span>
                  ))}
                  {preset.keywords.length > 4 && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      +{preset.keywords.length - 4}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          </button>
        ))}
      </div>
    </div>
  )
}
